import { PlexUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { Avatar, createStyles, IconButton, IconButtonProps, makeStyles, Theme, Tooltip } from '@material-ui/core';
import React from 'react';

import { PlexDomainSummaryFragment } from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { rolesRankingSort } from '../../../../utils/permissions';
import { LinesEllipsis } from '../../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { canLeave, canManage } from '../../permissions';
import { useSubscribeToPlex } from '../../plexes/useSnackbarSubscribe';
import { MenuOptionItem, MoreOptionsMenu } from '../../shared/MoreOptionsMenu';
import { useDialogState } from '../useDialogState';
import { SubscribeButton } from './SubscribeButton';

interface Props {
  plex: PlexDomainSummaryFragment;
}

export const PlexView = React.memo(
  ({ plex }: Props): React.ReactElement => {
    const classes = useStyles();
    const [, setDialogState] = useDialogState();
    const { onLeaveClick } = useSubscribeToPlex();

    const handleEdit = (id: string): void => {
      setDialogState({ type: 'edit_plex', plexId: id });
    };

    const handleLeave = (plex: PlexDomainSummaryFragment) => (id: string): void => {
      onLeaveClick(plex);
    };

    const maxAvatars = 2;
    const membersCount = plex.summary.members.length || 0;
    const plexSummaryMembers = [...plex.summary.members];

    return (
      <div className={classes.root}>
        <div className={classes.topContent}>
          <div className={classes.avatarsList}>
            {plexSummaryMembers
              .sort(rolesRankingSort)
              .slice(0, maxAvatars)
              .map((member) => {
                return (
                  <Tooltip key={`${member.picture}_${member.name}`} title={member.name}>
                    <Avatar className={classes.avatar} src={member.picture} alt={member.name} />
                  </Tooltip>
                );
              })}
            {membersCount > maxAvatars && (
              <Avatar className={classes.avatar}>{`+${Math.min(membersCount - maxAvatars, 99)}`}</Avatar>
            )}
          </div>
          <div className={classes.topContentRight}>
            <MoreOptionsMenu
              id={plex.id}
              options={moreOptions(plex.currentUserRelation.state, handleEdit, handleLeave(plex))}
              Button={MoreOptionsButton}
              menuProps={{ anchorOrigin: { horizontal: 'left', vertical: 'bottom' } }}
            />
          </div>
        </div>
        <div className={classes.plexNameContainer}>
          <PlexTypography className={classes.plexName} text={plex.name} size={TextSizesEnum.XLarge} />
          <div className={classes.plexColor} style={{ backgroundColor: plex.color || colorPalette.gray._400 }} />
        </div>
        <PlexTypography className={classes.plexDescription} size={TextSizesEnum.NormalPlus}>
          <LinesEllipsis text={plex.description || 'Missing description'} maxLine={2} />
        </PlexTypography>

        <SubscribeButton plex={plex} />
      </div>
    );
  }
);

function MoreOptionsButton(props: IconButtonProps): React.ReactElement {
  return <IconButton {...props} size={'small'} />;
}

function moreOptions(
  currentUserRelation: PlexUserRelationState,
  onEditClick: (id: string) => void,
  onLeaveClick: (id: string) => void
): MenuOptionItem[] {
  const options: MenuOptionItem[] = [];

  if (canManage(currentUserRelation)) {
    options.push({ label: 'Edit', onClick: onEditClick });
  }

  if (canLeave(currentUserRelation)) {
    options.push({
      label: currentUserRelation === PlexUserRelationState.Subscriber ? 'Unsubscribe' : 'Leave',
      onClick: onLeaveClick,
    });
  }

  return options;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: 345,
      height: 286,
      flexDirection: 'column',
      margin: spacings.xlarge / 2,
      border: '1px solid #EAEAEA',
      borderRadius: 6,
      backgroundColor: '#fff',
    },
    topContent: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: spacings.xlarge,
      marginLeft: spacings.xlarge,
    },
    avatarsList: {
      display: 'flex',
      flexDirection: 'row',
    },
    avatar: {
      display: 'flex',
      width: 24,
      height: 24,
      border: '1px solid #FFFFFF',
      fontSize: 8,
      lineHeight: '16px',
      fontWeight: 500,
      marginRight: -spacings.small,
    },
    topContentRight: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    plexName: {
      fontWeight: 'bold',
      color: '#000',
      marginLeft: spacings.xlarge,
      marginRight: spacings.xlarge,
    },
    plexDescription: {
      height: 48,
      color: colorPalette.purpleGray._500,
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap', // fixes bug where only ellipsis would show when text is too long
      marginTop: spacings.small,
      marginLeft: spacings.xlarge,
      marginRight: spacings.xlarge,
    },
    plexNameContainer: {
      marginTop: 48,
      display: 'flex',
      alignItems: 'center',
    },
    plexColor: {
      height: 20,
      width: 20,
      borderRadius: 10,
    },
  })
);
